import React, { Suspense, lazy, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { Lock } from 'lucide-react';
import { AuthProvider } from '@contexts/AuthContext';
import { ProfileProvider, useProfile } from '@contexts/ProfileContext';
import { ActivityModalProvider } from '@contexts/ActivityModalContext';
import { SocialPostProvider } from '@contexts/SocialPostContext';
import { loadConfig } from './config';

// Components
import { Login } from '@components/Login';
import { AuthCallback } from '@components/AuthCallback';
import { ProtectedRoute } from '@components/ProtectedRoute';
import { DashboardLayout } from '@components/DashboardLayout';
import { PermissionGuard } from '@components/PermissionGuard';

// Pages
import { DashboardView } from '@pages/dashboard/DashboardView';
import { KanbanView } from '@pages/kanban/KanbanView';
import { CalendarView } from '@pages/calendar/CalendarView';
import { ActivityListView } from '@pages/activities/ActivityListView';
import { RecurrenceListView } from '@pages/recurrences/RecurrenceListView';
import { CoverageView } from '@pages/coverage/CoverageView';
import { MediaLibraryView } from '@pages/media/MediaLibraryView';
import { MediaDuplicatesView } from '@pages/media/MediaDuplicatesView';
import { MediaTaxonomyView } from '@pages/media/MediaTaxonomyView';
import { MediaImportView } from '@pages/media/MediaImportView';
import { TeamAdminView } from '@pages/admin/TeamAdminView';
import { BoardAdminView } from '@pages/admin/BoardAdminView';
import { IntegrationsView } from '@pages/admin/IntegrationsView';
import { AIProvidersView } from '@pages/admin/ai/AIProvidersView';
import { AIAgentsView } from '@pages/admin/ai/AIAgentsView';
import { AIBudgetView } from '@pages/admin/ai/AIBudgetView';
import { AIUsageView } from '@pages/admin/ai/AIUsageView';
import { HelpCenterView } from '@pages/help/HelpCenterView';
import { SocialGridView } from '@pages/social/SocialGridView';
import { SocialCalendarView } from '@pages/social/SocialCalendarView';
import { SocialPostsView } from '@pages/social/SocialPostsView';
import { SocialApprovalsView } from '@pages/social/SocialApprovalsView';
import { SocialIdeasView } from '@pages/social/SocialIdeasView';
import { SocialHashtagsView } from '@pages/social/SocialHashtagsView';
import { SocialAccountsView } from '@pages/social/SocialAccountsView';
// Landing pages carregam sob demanda: o editor (GrapesJS) é grande e só essas telas o usam.
const LandingPagesView = lazy(() => import('@pages/landing/LandingPagesView'));
const LandingPageDetailView = lazy(() => import('@pages/landing/LandingPageDetailView'));
const LandingTemplateEditorView = lazy(() => import('@pages/landing/LandingTemplateEditorView'));
const EmailCampaignsView = lazy(() => import('@pages/email/EmailCampaignsView'));
const EmailCampaignDetailView = lazy(() => import('@pages/email/EmailCampaignDetailView'));
const EmailTemplateEditorView = lazy(() => import('@pages/email/EmailTemplateEditorView'));
const EmailLeadsView = lazy(() => import('@pages/email/EmailLeadsView'));
const EmailSenderAccountsView = lazy(() => import('@pages/admin/EmailSenderAccountsView'));
const lazyFallback = <div className="py-12 text-center text-sm text-slate-400">Carregando…</div>;

const AccessDenied: React.FC<{ message?: string }> = ({ message }) => (
  <div className="flex items-center justify-center py-24">
    <div className="text-center">
      <Lock size={48} className="text-slate-300 mx-auto mb-4" />
      <h2 className="text-xl font-semibold text-slate-700 mb-2">Acesso restrito</h2>
      <p className="text-slate-500">{message ?? 'Você não tem permissão para acessar esta página.'}</p>
    </div>
  </div>
);

/** Rota protegida por permissão (e, opcionalmente, por ser gestor/admin na hierarquia). */
const Guarded: React.FC<{ permission: string; teamManager?: boolean; children: React.ReactNode }> = ({
  permission,
  teamManager = false,
  children,
}) => {
  const { canManageTeam, isLoading } = useProfile();
  if (teamManager && !isLoading && !canManageTeam) {
    return <AccessDenied message="Disponível apenas para gestores (com subordinados na equipe) e administradores." />;
  }
  return (
    <PermissionGuard permission={permission} fallback={<AccessDenied />}>
      {children}
    </PermissionGuard>
  );
};

const AppContent: React.FC = () => (
  <Router>
    <Routes>
      <Route path="/login" element={<Login />} />
      <Route path="/auth/callback" element={<AuthCallback />} />

      <Route
        path="/"
        element={
          <ProtectedRoute>
            <ProfileProvider>
              <ActivityModalProvider>
                <SocialPostProvider>
                  <DashboardLayout />
                </SocialPostProvider>
              </ActivityModalProvider>
            </ProfileProvider>
          </ProtectedRoute>
        }
      >
        <Route index element={<Navigate to="/dashboard" replace />} />
        <Route path="dashboard" element={<Guarded permission="dashboard.read"><DashboardView /></Guarded>} />
        <Route path="kanban" element={<Guarded permission="activities.read"><KanbanView /></Guarded>} />
        <Route path="kanban/:boardId" element={<Guarded permission="activities.read"><KanbanView /></Guarded>} />
        <Route path="calendar" element={<Guarded permission="activities.read"><CalendarView /></Guarded>} />
        <Route path="activities" element={<Guarded permission="activities.read"><ActivityListView /></Guarded>} />
        <Route path="recurrences" element={<Guarded permission="recurrences.read"><RecurrenceListView /></Guarded>} />
        <Route
          path="coverage"
          element={<Guarded permission="coverage.manage" teamManager><CoverageView /></Guarded>}
        />
        <Route path="media" element={<Guarded permission="media.read"><MediaLibraryView /></Guarded>} />
        <Route path="media/duplicates" element={<Guarded permission="media.read"><MediaDuplicatesView /></Guarded>} />
        <Route path="media/taxonomy" element={<Guarded permission="media.read"><MediaTaxonomyView /></Guarded>} />
        <Route path="media/import" element={<Guarded permission="media.import.manage"><MediaImportView /></Guarded>} />
        <Route path="social/grid" element={<Guarded permission="social.read"><SocialGridView /></Guarded>} />
        <Route path="social/calendar" element={<Guarded permission="social.read"><SocialCalendarView /></Guarded>} />
        <Route path="social/posts" element={<Guarded permission="social.read"><SocialPostsView /></Guarded>} />
        <Route path="social/approvals" element={<Guarded permission="social.read"><SocialApprovalsView /></Guarded>} />
        <Route path="social/ideas" element={<Guarded permission="social.read"><SocialIdeasView /></Guarded>} />
        <Route path="social/hashtags" element={<Guarded permission="social.read"><SocialHashtagsView /></Guarded>} />
        <Route path="social/accounts" element={<Guarded permission="social.accounts.manage"><SocialAccountsView /></Guarded>} />
        <Route path="landing-pages" element={<Guarded permission="landing.read"><Suspense fallback={lazyFallback}><LandingPagesView /></Suspense></Guarded>} />
        <Route path="landing-pages/templates/:id" element={<Guarded permission="landing.manage"><Suspense fallback={lazyFallback}><LandingTemplateEditorView /></Suspense></Guarded>} />
        <Route path="landing-pages/:id" element={<Guarded permission="landing.read"><Suspense fallback={lazyFallback}><LandingPageDetailView /></Suspense></Guarded>} />
        <Route path="admin/team" element={<Guarded permission="team.manage"><TeamAdminView /></Guarded>} />
        <Route path="admin/boards" element={<Guarded permission="boards.manage"><BoardAdminView /></Guarded>} />
        <Route path="email" element={<Guarded permission="email.read"><Suspense fallback={lazyFallback}><EmailCampaignsView /></Suspense></Guarded>} />
        <Route path="email/leads" element={<Guarded permission="email.leads"><Suspense fallback={lazyFallback}><EmailLeadsView /></Suspense></Guarded>} />
        <Route path="email/templates/:id" element={<Guarded permission="email.manage"><Suspense fallback={lazyFallback}><EmailTemplateEditorView /></Suspense></Guarded>} />
        <Route path="email/:id" element={<Guarded permission="email.read"><Suspense fallback={lazyFallback}><EmailCampaignDetailView /></Suspense></Guarded>} />
        <Route path="admin/integrations" element={<Guarded permission="integrations.manage"><IntegrationsView /></Guarded>} />
        <Route path="admin/email-senders" element={<Guarded permission="email.configure"><Suspense fallback={lazyFallback}><EmailSenderAccountsView /></Suspense></Guarded>} />
        <Route path="admin/ai/providers" element={<Guarded permission="ai.configure"><AIProvidersView /></Guarded>} />
        <Route path="admin/ai/agents" element={<Guarded permission="ai.configure"><AIAgentsView /></Guarded>} />
        <Route path="admin/ai/budget" element={<Guarded permission="ai.configure"><AIBudgetView /></Guarded>} />
        <Route path="admin/ai/usage" element={<Guarded permission="ai.configure"><AIUsageView /></Guarded>} />
        <Route path="help" element={<HelpCenterView />} />
      </Route>

      <Route path="*" element={<Navigate to="/dashboard" replace />} />
    </Routes>
  </Router>
);

const AppLoading: React.FC = () => (
  <div className="min-h-screen bg-slate-50 flex items-center justify-center">
    <div className="text-center">
      <div className="w-12 h-12 bg-gradient-to-r from-emerald-600 to-emerald-700 rounded-xl flex items-center justify-center mx-auto mb-4">
        <div className="w-6 h-6 border-2 border-white border-t-transparent rounded-full animate-spin" />
      </div>
      <p className="text-slate-500">Carregando configurações…</p>
    </div>
  </div>
);

const AppError: React.FC<{ error: string }> = ({ error }) => (
  <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
    <div className="bg-white rounded-xl shadow-card p-8 max-w-md text-center">
      <h1 className="text-h3 mb-2">Não foi possível iniciar</h1>
      <p className="text-slate-500 mb-4">{error}</p>
      <button className="btn-primary" onClick={() => window.location.reload()}>
        Tentar novamente
      </button>
    </div>
  </div>
);

// App wrapper com providers e configuração
export default function App() {
  const [configLoaded, setConfigLoaded] = React.useState(false);
  const [configError, setConfigError] = React.useState<string | null>(null);

  useEffect(() => {
    loadConfig()
      .then(() => setConfigLoaded(true))
      .catch((error) => {
        const message = error instanceof Error ? error.message : 'Erro desconhecido';
        setConfigError(`Falha ao carregar configurações: ${message}`);
      });
  }, []);

  if (!configLoaded && !configError) return <AppLoading />;
  if (configError) return <AppError error={configError} />;

  return (
    <AuthProvider>
      <AppContent />
    </AuthProvider>
  );
}
